import { View, Text, StyleSheet } from '@react-pdf/renderer'
import { PDFReportFeatureProps } from '..'
import { pdfFontSize, pdfPadding } from '../_data/style-define'

const styles = StyleSheet.create({
  landPropertiesSection: {
    marginTop: pdfPadding.large,
    pageBreakBefore: '',
  },
  landPropertiesTitle: {
    color: '#00B5A3',
    fontSize: pdfFontSize.title,
    fontWeight: 'bold',
    marginBottom: pdfPadding.small,
  },
  tableSection: {
    marginBottom: pdfPadding.medium,
  },
  tableSectionTitle: {
    fontSize: pdfFontSize.subtitle,
    fontWeight: 'bold',
    marginBottom: pdfPadding.small,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: pdfPadding.small,
    marginLeft: pdfPadding.xLarge,
  },
  tableCell: {
    flex: 6,
    fontSize: pdfFontSize.text,
  },
  tableCellRight: {
    flex: 4,
    paddingLeft: pdfPadding.xLarge,
    fontSize: pdfFontSize.text,
    textAlign: 'left',
  },
})

const LandPropertiesUsage = ({
  data,
  ability,
  type,
}: PDFReportFeatureProps) => {
  if (type === 'parcel') {
    return (
      <View style={styles.landPropertiesSection}>
        <Text style={styles.landPropertiesTitle}>Land Properties & Usage</Text>

        {ability.apnDisplay && (
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              marginBottom: pdfPadding.small,
            }}
          >
            <Text
              style={{
                fontSize: pdfFontSize.subtitle,
                fontWeight: 'bold',
                color: '#00B5A3',
              }}
            >
              APN:{' '}
            </Text>
            <Text style={{ fontSize: pdfFontSize.subtitle, color: '#00B5A3' }}>
              {data.property.data.apn}
            </Text>
          </View>
        )}

        {ability.soilTypes && (
          <View style={styles.tableSection}>
            <Text style={styles.tableSectionTitle}>Soil Types</Text>
            {data.property.feature.properties.soil_composition.length === 0 && (
              <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
                <Text style={styles.tableCell}>
                  No soil data available for this parcel
                </Text>
              </View>
            )}

            {data.property.feature.properties.soil_composition.map(
              (soil: any, index: number) => (
                <View
                  key={soil['Common Name']}
                  style={[
                    styles.tableRow,
                    index ===
                    data.property.feature.properties.soil_composition.length - 1
                      ? { borderBottomWidth: 0 }
                      : {},
                  ]}
                >
                  <Text style={styles.tableCell}>{soil['Common Name']}</Text>
                  <Text style={styles.tableCellRight}>{soil.pct_cov} %</Text>
                </View>
              )
            )}
          </View>
        )}

        <View style={styles.tableSection}>
          <Text style={styles.tableSectionTitle}>Crop Data</Text>
          {(() => {
            const cropComposition =
              data.property.feature.properties.crop_composition
            const latestYear = Object.keys(cropComposition).sort().pop() // Get the most recent year
            if (latestYear) {
              const cropData = cropComposition[latestYear] || {} // Get data for the latest year

              const sortedArray = Object.entries(cropData).sort(
                ([, valueA], [, valueB]) =>
                  (valueB as number) - (valueA as number)
              )

              return sortedArray.map(([crop, percentage], index, array) => (
                <View
                  key={index}
                  style={[
                    styles.tableRow,
                    index === array.length - 1 ? { borderBottomWidth: 0 } : {},
                  ]}
                >
                  <Text style={styles.tableCell}>{crop}</Text>
                  <Text style={styles.tableCellRight}>
                    {Number(percentage).toFixed(2)} %
                  </Text>
                </View>
              ))
            } else {
              return (
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>
                    No Crop data for this parcel
                  </Text>
                </View>
              )
            }
          })()}
        </View>
      </View>
    )
  }

  return (
    <View style={styles.landPropertiesSection}>
      <Text style={styles.landPropertiesTitle}>Land Properties & Usage</Text>
      {Object.entries(data.parcels).map(([key, value]: any) => {
        const parcel = value
        return (
          <View
            key={`${key}-${parcel.data.apn}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: pdfPadding.small,
            }}
          >
            {ability.apnDisplay && (
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginBottom: 0,
                }}
              >
                <Text
                  style={{
                    fontSize: pdfFontSize.subtitle,
                    fontWeight: 'bold',
                    color: '#00B5A3',
                  }}
                >
                  APN:{' '}
                </Text>
                <Text
                  style={{ fontSize: pdfFontSize.subtitle, color: '#00B5A3' }}
                >
                  {parcel.data.apn}
                </Text>
              </View>
            )}

            {ability.soilTypes && (
              <View
                style={{
                  marginBottom: 0,
                }}
              >
                <Text style={styles.tableSectionTitle}>Soil Types</Text>
                {parcel.feature.properties.soil_composition?.length === 0 && (
                  <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
                    <Text style={styles.tableCell}>
                      No soil data available for this parcel
                    </Text>
                  </View>
                )}

                {parcel.feature.properties.soil_composition?.length > 0 &&
                  parcel.feature.properties.soil_composition?.map(
                    (soil: any, index: number) => (
                      <View
                        key={soil['Common Name']}
                        style={[
                          styles.tableRow,
                          index ===
                          parcel.feature.properties.soil_composition.length - 1
                            ? { borderBottomWidth: 0 }
                            : {},
                        ]}
                      >
                        <Text style={styles.tableCell}>
                          {soil['Common Name']}
                        </Text>
                        <Text style={styles.tableCellRight}>
                          {soil.pct_cov} %
                        </Text>
                      </View>
                    )
                  )}
              </View>
            )}
            <View style={styles.tableSection}>
              <Text style={styles.tableSectionTitle}>Crop Data</Text>
              {(() => {
                const cropComposition =
                  parcel.feature.properties.crop_composition
                if (
                  !cropComposition ||
                  Object.keys(cropComposition).length === 0
                ) {
                  return (
                    <View style={styles.tableRow}>
                      <Text style={styles.tableCell}>
                        No Crop data for this parcel
                      </Text>
                    </View>
                  )
                }

                const latestYear = Object.keys(cropComposition).sort().pop() // Get the most recent year
                if (latestYear) {
                  const cropData = cropComposition[latestYear] || {} // Get data for the latest year

                  const sortedArray = Object.entries(cropData).sort(
                    ([, valueA], [, valueB]) =>
                      (valueB as number) - (valueA as number)
                  )
                  return sortedArray.map(([crop, percentage], index, array) => (
                    <View
                      key={index}
                      style={[
                        styles.tableRow,
                        index === array.length - 1
                          ? { borderBottomWidth: 0 }
                          : {},
                      ]}
                    >
                      <Text style={styles.tableCell}>{crop}</Text>
                      <Text style={styles.tableCellRight}>
                        {Number(percentage).toFixed(2)} %
                      </Text>
                    </View>
                  ))
                } else {
                  return (
                    <View style={styles.tableRow}>
                      <Text style={styles.tableCell}>
                        No Crop data for this parcel
                      </Text>
                    </View>
                  )
                }
              })()}
            </View>
          </View>
        )
      })}
    </View>
  )
}

export default LandPropertiesUsage
